import {
  type AskPunttQuestionOption,
  type AskPunttUserQuestion,
} from "@mg/schemas/src/christo/askPuntt";
import { Paperclip, Upload, X, CircleNotch } from "@phosphor-icons/react";
import { Flex, Text, TextField } from "@radix-ui/themes";
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";

import { ChoiceRow } from "./ChoiceRow";
import { QuestionFormCard } from "./QuestionFormCard";

import { uploadFile } from "../../../services/upload";
import { useFileDrop } from "../hooks/useFileDrop";

export type QuestionAttachment = {
  key: string;
  source: string;
  type: "image" | "video" | "file";
  name?: string;
};

export type QuestionFormHandle = {
  /** Submit only if the user has provided at least one answer. */
  submit: () => void;
  /** Submit whatever answers are currently filled (or none), always succeeds. */
  submitAny: () => void;
};

interface QuestionFormProps {
  questions: AskPunttUserQuestion[];
  onSubmit: (
    formattedAnswers: string,
    attachments?: QuestionAttachment[],
  ) => void;
  disabled?: boolean;
  readOnly?: boolean;
  /** Hide the inline "Submit Answers" button (parent renders it externally). */
  hideSubmitButton?: boolean;
  /** Called whenever the "has any answer" state changes. */
  onAnswerChange?: (hasAnswer: boolean) => void;
  /**
   * When provided, only renders the question at this index (0-based).
   * Undefined = render all questions (read-only history mode).
   */
  visibleQuestionIndex?: number;
  /**
   * Called when the answer state of the currently visible question changes.
   * Used in step mode to enable/disable the "Next" button in the parent.
   */
  onCurrentQuestionAnswerChange?: (hasAnswer: boolean) => void;
  /**
   * Called when an option with isUpload=true is clicked so the parent can
   * open its own file dialog.
   */
  onUploadRequest?: () => void;
  /** Number of files already attached at the chat level (e.g. via drag-and-drop). */
  chatLevelFileCount?: number;
  /**
   * Override the default `data-testid` ("question-form") for callers that
   * need to disambiguate multiple QuestionForm instances on the same screen.
   */
  "data-testid"?: string;
  /**
   * `grid` = 2-up from `sm` (existing chat packing).
   * `stacked` = full-width ChoiceRows matching Figma QuestionForm.
   */
  optionLayout?: "grid" | "stacked";
  /**
   * When true, cards use plain chrome so the parent composer owns the surface
   * (white fill, rounded-2xl, focus-within accent) — no nested QuestionForm shell.
   */
  embedded?: boolean;
}

type Selections = Record<number, Set<string>>;
type FreeTexts = Record<number, string>;

type FileItem = {
  id: string;
  file: File;
  status: "pending" | "done" | "error";
  attachment?: QuestionAttachment;
};
type PerQuestionFiles = Record<number, FileItem[]>;

function optionKey(option: AskPunttQuestionOption): string {
  return option.value ?? option.label;
}

function deriveType(file: File): "image" | "video" | "file" {
  if (file.type.startsWith("image/")) return "image";
  if (file.type.startsWith("video/")) return "video";
  return "file";
}

export const QuestionForm = forwardRef<QuestionFormHandle, QuestionFormProps>(
  function QuestionForm(
    {
      questions,
      onSubmit,
      disabled,
      readOnly,
      hideSubmitButton,
      onAnswerChange,
      visibleQuestionIndex,
      onCurrentQuestionAnswerChange,
      onUploadRequest,
      chatLevelFileCount = 0,
      "data-testid": dataTestId = "question-form",
      optionLayout = "grid",
      embedded = false,
    },
    ref,
  ) {
    const { t } = useTranslation("ui");
    const [selections, setSelections] = useState<Selections>(() => {
      const init: Selections = {};
      questions.forEach((_, i) => {
        init[i] = new Set();
      });
      return init;
    });
    const [freeTexts, setFreeTexts] = useState<FreeTexts>(() => {
      const init: FreeTexts = {};
      questions.forEach((_, i) => {
        init[i] = "";
      });
      return init;
    });
    const [submitted, setSubmitted] = useState(readOnly ?? false);
    const [files, setFiles] = useState<PerQuestionFiles>({});
    const fileInputRefs = useRef<Record<number, HTMLInputElement | null>>({});
    const textInputRefs = useRef<Record<number, HTMLInputElement | null>>({});

    useEffect(() => {
      if (!readOnly) setSubmitted(false);
    }, [readOnly]);

    // Auto-focus the text input when a new question becomes visible
    useEffect(() => {
      if (
        !submitted &&
        !disabled &&
        visibleQuestionIndex !== undefined &&
        textInputRefs.current[visibleQuestionIndex]
      ) {
        // Small delay to ensure the input is fully rendered
        const timer = setTimeout(() => {
          textInputRefs.current[visibleQuestionIndex]?.focus();
        }, 100);
        return () => clearTimeout(timer);
      }
    }, [visibleQuestionIndex, submitted, disabled]);

    const toggleOption = useCallback(
      (qIdx: number, opt: AskPunttQuestionOption, multiSelect?: boolean) => {
        if (submitted || disabled) return;
        const key = optionKey(opt);
        setSelections((prev) => {
          const current = new Set(prev[qIdx]);
          if (multiSelect) {
            if (current.has(key)) current.delete(key);
            else current.add(key);
          } else {
            if (current.has(key)) current.clear();
            else {
              current.clear();
              current.add(key);
            }
          }
          return { ...prev, [qIdx]: current };
        });
        if (opt.isUpload) {
          onUploadRequest?.();
        }
      },
      [submitted, disabled, onUploadRequest],
    );

    const handleFreeText = useCallback(
      (qIdx: number, value: string) => {
        if (submitted || disabled) return;
        setFreeTexts((prev) => ({ ...prev, [qIdx]: value }));
      },
      [submitted, disabled],
    );

    const handleAddFiles = useCallback(
      (qIdx: number, newFiles: FileList | null) => {
        if (!newFiles || newFiles.length === 0 || submitted || disabled) return;

        const items: FileItem[] = Array.from(newFiles).map((f) => ({
          id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
          file: f,
          status: "pending" as const,
        }));

        setFiles((prev) => ({
          ...prev,
          [qIdx]: [...(prev[qIdx] ?? []), ...items],
        }));

        items.forEach((item) => {
          uploadFile(item.file)
            .then((r) => {
              setFiles((prev) => ({
                ...prev,
                [qIdx]: (prev[qIdx] ?? []).map((p) =>
                  p.id === item.id
                    ? {
                        ...p,
                        status: "done" as const,
                        attachment: {
                          key: r.key,
                          source: r.location,
                          type: deriveType(p.file),
                          name: p.file.name,
                        },
                      }
                    : p,
                ),
              }));
            })
            .catch(() => {
              setFiles((prev) => ({
                ...prev,
                [qIdx]: (prev[qIdx] ?? []).map((p) =>
                  p.id === item.id ? { ...p, status: "error" as const } : p,
                ),
              }));
            });
        });
      },
      [submitted, disabled],
    );

    const removeFile = useCallback(
      (qIdx: number, fileId: string) => {
        if (submitted || disabled) return;
        setFiles((prev) => ({
          ...prev,
          [qIdx]: (prev[qIdx] ?? []).filter((f) => f.id !== fileId),
        }));
      },
      [submitted, disabled],
    );

    const hasPendingUploads = Object.values(files).some((items) =>
      items.some((f) => f.status === "pending"),
    );

    const questionHasUploadOption = useCallback(
      (qIdx: number) =>
        !!questions[qIdx]?.allowFiles ||
        (questions[qIdx]?.options ?? []).some((o) => o.isUpload),
      [questions],
    );

    const hasChatFiles = chatLevelFileCount > 0;

    // Auto-select the upload option when chat-level files are dragged in
    useEffect(() => {
      if (!hasChatFiles || submitted || disabled) return;
      questions.forEach((q, qIdx) => {
        const uploadOpt = (q.options ?? []).find((o) => o.isUpload);
        if (!uploadOpt) return;
        setSelections((prev) => {
          const key = optionKey(uploadOpt);
          if (prev[qIdx]?.has(key)) return prev;
          const next = new Set(prev[qIdx]);
          next.add(key);
          return { ...prev, [qIdx]: next };
        });
      });
    }, [hasChatFiles, questions, submitted, disabled]);

    const hasAnyAnswer = questions.some((_, i) => {
      const hasSelection = (selections[i]?.size ?? 0) > 0;
      const hasText = !!freeTexts[i]?.trim();
      const hasFiles = (files[i] ?? []).some(
        (f) => f.status === "done" && f.attachment,
      );
      const hasChatLevelFiles = hasChatFiles && questionHasUploadOption(i);
      return hasSelection || hasText || hasFiles || hasChatLevelFiles;
    });

    // Per-question answer state for step mode
    const currentIdx = visibleQuestionIndex ?? 0;
    const chatFilesCountForCurrent =
      hasChatFiles && questionHasUploadOption(currentIdx);
    const hasCurrentAnswer =
      (selections[currentIdx]?.size ?? 0) > 0 ||
      !!freeTexts[currentIdx]?.trim() ||
      (files[currentIdx] ?? []).some(
        (f) => f.status === "done" && f.attachment,
      ) ||
      !!chatFilesCountForCurrent;

    // Notify parent when overall answer availability changes
    useEffect(() => {
      onAnswerChange?.(hasAnyAnswer);
    }, [hasAnyAnswer, onAnswerChange]);

    // Notify parent when the current visible question's answer changes (step mode)
    useEffect(() => {
      if (visibleQuestionIndex !== undefined) {
        onCurrentQuestionAnswerChange?.(hasCurrentAnswer);
      }
    }, [hasCurrentAnswer, visibleQuestionIndex, onCurrentQuestionAnswerChange]);

    const buildResult = useCallback((): {
      formatted: string;
      attachments: QuestionAttachment[];
    } => {
      const lines: string[] = [];
      const allAttachments: QuestionAttachment[] = [];

      questions.forEach((q, i) => {
        const selected = Array.from(selections[i] ?? []).map((key) => {
          const option = (q.options ?? []).find(
            (candidate) => optionKey(candidate) === key,
          );
          if (!option) return key;
          return option.value
            ? `${option.label} (selection: ${option.value})`
            : option.label;
        });
        const free = freeTexts[i]?.trim();
        const selectedText = selected.join(", ");
        // Both an explicit selection and typed free text can coexist (the UI
        // never clears one when the other is set). Keep both so the chosen
        // option is not silently dropped from the submitted answer.
        const answer = [selectedText, free].filter(Boolean).join(" — ");

        const doneFiles = (files[i] ?? [])
          .filter((f) => f.status === "done" && f.attachment)
          .map((f) => f.attachment!);

        doneFiles.forEach((a) => allAttachments.push(a));

        if (!answer && doneFiles.length === 0) return; // omit fully unanswered questions

        const filesStr =
          doneFiles.length > 0
            ? `\nFiles: ${doneFiles.map((a) => a.name ?? a.source).join(", ")}`
            : "";

        if (answer) {
          lines.push(`**${q.header}:** ${answer}${filesStr}`);
        } else {
          lines.push(`**${q.header}:** ${filesStr.trim()}`);
        }
      });

      return {
        formatted: lines.join("\n"),
        attachments: allAttachments,
      };
    }, [questions, selections, freeTexts, files]);

    const handleSubmit = useCallback(() => {
      if (!hasAnyAnswer || submitted || disabled || hasPendingUploads) return;
      const { formatted, attachments } = buildResult();
      setSubmitted(true);
      onSubmit(formatted, attachments.length > 0 ? attachments : undefined);
    }, [
      hasAnyAnswer,
      submitted,
      disabled,
      hasPendingUploads,
      buildResult,
      onSubmit,
    ]);

    const handleSubmitAny = useCallback(() => {
      if (submitted || disabled) return;
      setSubmitted(true);
      if (!hasAnyAnswer) {
        onSubmit("Please just get started.", undefined);
        return;
      }
      const { formatted, attachments } = buildResult();
      onSubmit(formatted, attachments.length > 0 ? attachments : undefined);
    }, [submitted, disabled, hasAnyAnswer, buildResult, onSubmit]);

    useImperativeHandle(
      ref,
      () => ({ submit: handleSubmit, submitAny: handleSubmitAny }),
      [handleSubmit, handleSubmitAny],
    );

    // why: after submit the parent swaps back to the primary composer;
    // QuestionFormAnswer chips render on the user message in the feed.
    if (submitted && !readOnly) {
      return null;
    }

    // In step mode, only render the visible question; otherwise render all
    const questionsToRender =
      visibleQuestionIndex !== undefined
        ? questions
            .map((q, i) => ({ q, i }))
            .filter(({ i }) => i === visibleQuestionIndex)
        : questions.map((q, i) => ({ q, i }));
    const lastRenderedIdx = questionsToRender[questionsToRender.length - 1]?.i;
    const showFormSubmit =
      !submitted && !hideSubmitButton && visibleQuestionIndex === undefined;

    return (
      <div
        data-testid={dataTestId}
        className={
          embedded
            ? "animate-fade-in space-y-3"
            : "mt-3 animate-fade-in space-y-3"
        }
      >
        {questionsToRender.map(({ q, i: qIdx }) => {
          const selected = selections[qIdx] ?? new Set();
          const freeText = freeTexts[qIdx] ?? "";
          const questionFiles = files[qIdx] ?? [];
          const showFileUpload = q.allowFiles && !submitted;
          const isLastCard = qIdx === lastRenderedIdx;
          const testIdPrefix = `question-card-${qIdx}`;

          const optionRows = (
            <div
              role="group"
              aria-label={q.header}
              data-testid="question-options-grid"
              className={
                optionLayout === "stacked"
                  ? "grid grid-cols-1 items-start gap-2"
                  : "grid grid-cols-1 items-start gap-2 sm:grid-cols-2"
              }
            >
              {(q.options ?? []).map((opt) => {
                const key = optionKey(opt);
                const isSelected = selected.has(key);
                const showUploadMore =
                  opt.isUpload && hasChatFiles && isSelected;
                const displayLabel = showUploadMore
                  ? t("uploadMore")
                  : opt.label;
                const displayDesc = showUploadMore
                  ? t("questionForm.fileAlreadyAttached", {
                      count: chatLevelFileCount,
                    })
                  : opt.description;
                return (
                  <ChoiceRow
                    key={key}
                    data-testid="question-option"
                    label={displayLabel}
                    description={displayDesc}
                    selected={isSelected}
                    disabled={submitted || disabled}
                    onClick={() => toggleOption(qIdx, opt, q.multiSelect)}
                  />
                );
              })}
            </div>
          );

          const fileChips =
            questionFiles.length > 0 ? (
              <Flex gap="2" wrap="wrap" className="mt-2">
                {questionFiles.map((item) => (
                  <Flex
                    key={item.id}
                    align="center"
                    gap="1"
                    className="rounded-lg border border-puntt-neutral-gray-6 bg-base-white px-2 py-1"
                  >
                    {item.status === "pending" && (
                      <CircleNotch
                        size={12}
                        className="animate-spin text-puntt-accent-9"
                      />
                    )}
                    <Text
                      size="1"
                      className="max-w-[150px] truncate text-puntt-neutral-gray-11"
                    >
                      {item.file.name}
                      {item.status === "error" && " (failed)"}
                    </Text>
                    {!submitted && (
                      <button
                        data-testid="question-remove-file-btn"
                        type="button"
                        aria-label={t("questionForm.removeFile", {
                          name: item.file.name,
                        })}
                        onClick={() => removeFile(qIdx, item.id)}
                        className="ml-0.5 text-puntt-neutral-gray-8 hover:text-puntt-neutral-gray-12"
                      >
                        <X size={12} aria-hidden="true" />
                      </button>
                    )}
                  </Flex>
                ))}
              </Flex>
            ) : null;

          const slot = (
            <>
              {optionRows}
              {!submitted ? (
                <Flex align="center" gap="2" className="mt-2">
                  <div className="flex-1">
                    <TextField.Root
                      data-testid="question-text-input"
                      size="2"
                      variant="surface"
                      placeholder={t("orTypeYourOwn")}
                      aria-label={t("orTypeYourOwn")}
                      value={freeText}
                      onChange={(e) => handleFreeText(qIdx, e.target.value)}
                      ref={(el) => {
                        if (el) {
                          const input = el.querySelector("input");
                          textInputRefs.current[qIdx] = input;
                        }
                      }}
                    />
                  </div>
                  {showFileUpload && (
                    <>
                      <input
                        data-testid="question-form-input-471"
                        ref={(el) => {
                          fileInputRefs.current[qIdx] = el;
                        }}
                        type="file"
                        multiple
                        className="hidden"
                        aria-hidden="true"
                        onChange={(e) => {
                          handleAddFiles(qIdx, e.target.files);
                          e.target.value = "";
                        }}
                      />
                      <button
                        data-testid="question-attach-btn"
                        type="button"
                        disabled={disabled}
                        onClick={() => fileInputRefs.current[qIdx]?.click()}
                        className="flex shrink-0 items-center justify-center rounded-lg border border-puntt-neutral-gray-6 bg-base-white p-2 text-puntt-neutral-gray-9 transition-colors hover:border-puntt-accent-7 hover:text-puntt-accent-9"
                        title={t("attachFiles")}
                        aria-label={t("attachFiles")}
                      >
                        <Paperclip size={16} aria-hidden="true" />
                      </button>
                    </>
                  )}
                </Flex>
              ) : null}
              {showFileUpload && !submitted && (
                <QuestionDropZone
                  qIdx={qIdx}
                  disabled={disabled}
                  fileInputRef={(el) => {
                    fileInputRefs.current[qIdx] = el;
                  }}
                  onAddFiles={(dropped) => handleAddFiles(qIdx, dropped)}
                />
              )}
              {fileChips}
            </>
          );

          return (
            <QuestionFormCard
              key={qIdx}
              testId="question-card"
              testIdPrefix={testIdPrefix}
              questionIndex={qIdx}
              chrome={embedded ? "plain" : "shell"}
              title={q.question}
              detail1={q.header}
              primary={
                isLastCard && showFormSubmit
                  ? {
                      action: "submit",
                      label: hasPendingUploads
                        ? t("uploading")
                        : t("submitAnswers"),
                      disabled: !hasAnyAnswer || disabled || hasPendingUploads,
                      onClick: handleSubmit,
                    }
                  : undefined
              }
            >
              {slot}
            </QuestionFormCard>
          );
        })}
      </div>
    );
  },
);

function QuestionDropZone({
  qIdx,
  disabled,
  fileInputRef,
  onAddFiles,
}: {
  qIdx: number;
  disabled?: boolean;
  fileInputRef: (el: HTMLInputElement | null) => void;
  onAddFiles: (files: FileList | null) => void;
}) {
  const { t } = useTranslation("ui");
  const inputRef = useRef<HTMLInputElement | null>(null);
  const { isDragOver, dropZoneProps } = useFileDrop({
    onDrop: (files) => {
      if (disabled) return;
      const dt = new DataTransfer();
      files.forEach((f) => dt.items.add(f));
      onAddFiles(dt.files);
    },
  });

  return (
    <div className="mt-2">
      <input
        data-testid="question-form-input-595"
        ref={(el) => {
          inputRef.current = el;
          fileInputRef(el);
        }}
        type="file"
        multiple
        className="hidden"
        aria-hidden="true"
        onChange={(e) => {
          onAddFiles(e.target.files);
          e.target.value = "";
        }}
      />
      <button
        type="button"
        data-testid={`question-drop-zone-${qIdx}`}
        disabled={disabled}
        onClick={() => inputRef.current?.click()}
        className={`flex w-full cursor-pointer flex-col items-center gap-2 rounded-xl border-2 border-dashed px-4 py-6 transition-colors ${
          isDragOver
            ? "border-puntt-accent-7 bg-puntt-accent-2"
            : "border-puntt-neutral-gray-6 bg-base-white hover:border-puntt-accent-7 hover:bg-puntt-accent-2/30"
        } ${disabled ? "cursor-default opacity-50" : ""}`}
        {...dropZoneProps}
      >
        <Upload
          size={24}
          aria-hidden="true"
          className={
            isDragOver ? "text-puntt-accent-9" : "text-puntt-neutral-gray-9"
          }
        />
        <Text size="2" className="text-puntt-neutral-gray-11">
          {t("dragDropHint")}
        </Text>
      </button>
    </div>
  );
}
